/**
 * Additional Industry Templates (Part 2)
 * Healthcare, Professional Services, Food & Beverage, Manufacturing, etc.
 */

import { IndustryTemplate } from '../types/industryTemplates';
import { SectionId } from '../types/businessPlan';

const createTemplate = (
  id: string,
  name: string,
  category: any,
  description: string,
  icon: string,
  popularity: number,
  keyMetrics: string[],
  revenueStreams: any[],
  expenses: any[],
  margins: any,
  startupCosts: any
): IndustryTemplate => ({
  id, name, category, description, icon, popularity, isActive: true,
  sectionTemplates: {},
  smartPrompts: { industryQuestions: [], editingHints: {} as Partial<Record<SectionId, string[]>> },
  financialDefaults: { typicalRevenueStreams: revenueStreams, typicalExpenses: expenses, benchmarks: margins, typicalStartupCosts: startupCosts },
  keyMetrics, commonChallenges: []
});

export const MORE_TEMPLATES: IndustryTemplate[] = [
  // FOOD & BEVERAGE (5 more)
  createTemplate('bakery', 'Bakery', 'food-beverage', 'Retail or wholesale bakery', '🥖', 67,
    ['Daily Sales', 'Waste %', 'Labor Cost %', 'Customer Repeat Rate'],
    [{ name: 'Retail Sales', category: 'product', avgUnitPrice: 8, avgUnitsPerMonth: 3000, growthRate: 12 }],
    [{ name: 'Ingredients', category: 'variable', avgAmount: 0, percentage: 28 }, { name: 'Labor', category: 'fixed', avgAmount: 10000 }],
    { grossMargin: { min: 65, avg: 72, max: 78 }, netMargin: { min: 8, avg: 15, max: 22 }, revenueGrowth: { min: 10, avg: 18, max: 30 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 40000, medium: 120000, high: 300000 }
  ),
  
  createTemplate('brewery', 'Brewery / Craft Beer', 'food-beverage', 'Craft beer production and taproom', '🍺', 70,
    ['Barrels Produced', 'Distribution Accounts', 'Taproom Revenue', 'Cost per Barrel'],
    [{ name: 'Beer Sales', category: 'product', avgUnitPrice: 150, avgUnitsPerMonth: 200, growthRate: 18 }],
    [{ name: 'Ingredients', category: 'variable', avgAmount: 0, percentage: 25 }, { name: 'Equipment', category: 'fixed', avgAmount: 3000 }],
    { grossMargin: { min: 60, avg: 70, max: 78 }, netMargin: { min: 10, avg: 20, max: 30 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 18, avg: 24, max: 36 }},
    { low: 250000, medium: 750000, high: 2000000 }
  ),
  
  createTemplate('meal-prep', 'Meal Prep Service', 'food-beverage', 'Prepared meal delivery service', '🍱', 73,
    ['Active Subscribers', 'Meals per Week', 'Churn Rate', 'Food Cost %'],
    [{ name: 'Weekly Subscriptions', category: 'subscription', avgUnitPrice: 80, avgUnitsPerMonth: 500, growthRate: 22 }],
    [{ name: 'Food Costs', category: 'variable', avgAmount: 0, percentage: 35 }, { name: 'Delivery', category: 'variable', avgAmount: 0, percentage: 12 }],
    { grossMargin: { min: 48, avg: 53, max: 60 }, netMargin: { min: 8, avg: 15, max: 22 }, revenueGrowth: { min: 20, avg: 35, max: 60 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 40000, medium: 120000, high: 350000 }
  ),
  
  createTemplate('ghost-kitchen', 'Ghost Kitchen', 'food-beverage', 'Delivery-only restaurant', '👻', 75,
    ['Orders per Day', 'Average Order Value', 'Delivery Time', 'Food Cost %'],
    [{ name: 'Delivery Orders', category: 'product', avgUnitPrice: 22, avgUnitsPerMonth: 2500, growthRate: 28 }],
    [{ name: 'Food Costs', category: 'variable', avgAmount: 0, percentage: 32 }, { name: 'Delivery Fees', category: 'variable', avgAmount: 0, percentage: 25 }],
    { grossMargin: { min: 40, avg: 43, max: 50 }, netMargin: { min: 5, avg: 12, max: 18 }, revenueGrowth: { min: 25, avg: 40, max: 80 }, breakEvenMonths: { min: 8, avg: 12, max: 18 }},
    { low: 30000, medium: 80000, high: 200000 }
  ),
  
  createTemplate('bar-nightclub', 'Bar / Nightclub', 'food-beverage', 'Bar, lounge, or nightclub venue', '🍸', 64,
    ['Nightly Revenue', 'Bar Sales %', 'Cover Charge', 'Cost per Pour'],
    [{ name: 'Beverage Sales', category: 'product', avgUnitPrice: 12, avgUnitsPerMonth: 4000, growthRate: 15 }],
    [{ name: 'Liquor Costs', category: 'variable', avgAmount: 0, percentage: 22 }, { name: 'Staff', category: 'fixed', avgAmount: 15000 }],
    { grossMargin: { min: 70, avg: 78, max: 85 }, netMargin: { min: 8, avg: 15, max: 25 }, revenueGrowth: { min: 10, avg: 18, max: 30 }, breakEvenMonths: { min: 15, avg: 20, max: 30 }},
    { low: 150000, medium: 400000, high: 1000000 }
  ),
  
  // HEALTHCARE (4 more)
  createTemplate('physical-therapy', 'Physical Therapy', 'healthcare', 'Physical therapy and rehabilitation clinic', '🏃', 72,
    ['Patient Visits', 'Treatment Plans', 'Insurance Mix', 'Patient Outcomes'],
    [{ name: 'Therapy Sessions', category: 'service', avgUnitPrice: 120, avgUnitsPerMonth: 400, growthRate: 12 }],
    [{ name: 'Therapist Salaries', category: 'fixed', avgAmount: 22000 }, { name: 'Equipment', category: 'fixed', avgAmount: 2000 }],
    { grossMargin: { min: 55, avg: 65, max: 72 }, netMargin: { min: 18, avg: 28, max: 35 }, revenueGrowth: { min: 10, avg: 15, max: 25 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 80000, medium: 200000, high: 500000 }
  ),
  
  createTemplate('mental-health', 'Mental Health Practice', 'healthcare', 'Therapy and counseling services', '🧠', 78,
    ['Client Sessions', 'Retention Rate', 'Session Utilization', 'No-Show Rate'],
    [{ name: 'Therapy Sessions', category: 'service', avgUnitPrice: 150, avgUnitsPerMonth: 300, growthRate: 15 }],
    [{ name: 'Therapist Compensation', category: 'variable', avgAmount: 27000 }, { name: 'EHR Software', category: 'fixed', avgAmount: 500 }],
    { grossMargin: { min: 55, avg: 62, max: 70 }, netMargin: { min: 20, avg: 30, max: 40 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 30000, medium: 80000, high: 200000 }
  ),
  
  createTemplate('pharmacy', 'Pharmacy', 'healthcare', 'Retail or compounding pharmacy', '💊', 68,
    ['Prescriptions Filled', 'Gross Margin', 'Insurance Claims', 'Generic Rate'],
    [{ name: 'Prescription Sales', category: 'product', avgUnitPrice: 45, avgUnitsPerMonth: 3000, growthRate: 8 }],
    [{ name: 'Drug Costs', category: 'variable', avgAmount: 0, percentage: 75 }, { name: 'Pharmacist', category: 'fixed', avgAmount: 18000 }],
    { grossMargin: { min: 22, avg: 25, max: 30 }, netMargin: { min: 3, avg: 8, max: 12 }, revenueGrowth: { min: 5, avg: 10, max: 18 }, breakEvenMonths: { min: 18, avg: 24, max: 36 }},
    { low: 200000, medium: 500000, high: 1200000 }
  ),
  
  createTemplate('med-spa', 'Medical Spa', 'healthcare', 'Aesthetic and wellness treatments', '💆', 76,
    ['Treatment Revenue', 'Product Sales', 'Client Retention', 'Average Ticket'],
    [{ name: 'Treatments', category: 'service', avgUnitPrice: 250, avgUnitsPerMonth: 200, growthRate: 18 }],
    [{ name: 'Product Costs', category: 'variable', avgAmount: 0, percentage: 20 }, { name: 'Staff', category: 'fixed', avgAmount: 15000 }],
    { grossMargin: { min: 70, avg: 80, max: 88 }, netMargin: { min: 20, avg: 35, max: 50 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 150000, medium: 400000, high: 900000 }
  ),
  
  // PROFESSIONAL SERVICES (5 more)
  createTemplate('law-firm', 'Law Firm', 'professional-services', 'Legal services and representation', '⚖️', 75,
    ['Billable Hours', 'Realization Rate', 'Client Acquisition', 'Case Win Rate'],
    [{ name: 'Legal Fees', category: 'service', avgUnitPrice: 300, avgUnitsPerMonth: 200, growthRate: 12 }],
    [{ name: 'Attorney Salaries', category: 'fixed', avgAmount: 35000 }, { name: 'Research Tools', category: 'fixed', avgAmount: 2000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 22, avg: 35, max: 48 }, revenueGrowth: { min: 10, avg: 18, max: 30 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 50000, medium: 150000, high: 400000 }
  ),
  
  createTemplate('architecture', 'Architecture Firm', 'professional-services', 'Architectural design services', '🏛️', 69,
    ['Project Value', 'Design Hours', 'Client Satisfaction', 'Project Completion'],
    [{ name: 'Design Fees', category: 'service', avgUnitPrice: 25000, avgUnitsPerMonth: 4, growthRate: 15 }],
    [{ name: 'Architect Salaries', category: 'fixed', avgAmount: 28000 }, { name: 'Software', category: 'fixed', avgAmount: 1500 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 18, avg: 28, max: 40 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 75000, medium: 200000, high: 500000 }
  ),
  
  createTemplate('recruiting', 'Recruiting Agency', 'professional-services', 'Talent acquisition and placement', '👔', 74,
    ['Placements', 'Time to Fill', 'Client Retention', 'Candidate Quality'],
    [{ name: 'Placement Fees', category: 'service', avgUnitPrice: 15000, avgUnitsPerMonth: 5, growthRate: 18 }],
    [{ name: 'Recruiter Salaries', category: 'fixed', avgAmount: 18000 }, { name: 'Job Boards', category: 'fixed', avgAmount: 1500 }],
    { grossMargin: { min: 60, avg: 70, max: 80 }, netMargin: { min: 22, avg: 35, max: 50 }, revenueGrowth: { min: 15, avg: 25, max: 45 }, breakEvenMonths: { min: 8, avg: 12, max: 18 }},
    { low: 30000, medium: 100000, high: 300000 }
  ),
  
  createTemplate('pr-agency', 'PR Agency', 'professional-services', 'Public relations and communications', '📢', 70,
    ['Active Clients', 'Media Placements', 'Client Retention', 'Campaign ROI'],
    [{ name: 'Monthly Retainers', category: 'subscription', avgUnitPrice: 4000, avgUnitsPerMonth: 8, growthRate: 15 }],
    [{ name: 'Team Salaries', category: 'fixed', avgAmount: 22000 }, { name: 'Media Tools', category: 'fixed', avgAmount: 800 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 18, avg: 28, max: 38 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 40000, medium: 120000, high: 300000 }
  ),
  
  createTemplate('photography', 'Photography Studio', 'professional-services', 'Professional photography services', '📸', 66,
    ['Bookings', 'Average Shoot Value', 'Client Referrals', 'Editing Turnaround'],
    [{ name: 'Photo Sessions', category: 'service', avgUnitPrice: 800, avgUnitsPerMonth: 20, growthRate: 12 }],
    [{ name: 'Equipment', category: 'fixed', avgAmount: 1500 }, { name: 'Marketing', category: 'fixed', avgAmount: 1000 }],
    { grossMargin: { min: 65, avg: 75, max: 85 }, netMargin: { min: 25, avg: 40, max: 55 }, revenueGrowth: { min: 10, avg: 18, max: 30 }, breakEvenMonths: { min: 6, avg: 10, max: 15 }},
    { low: 15000, medium: 50000, high: 120000 }
  ),
  
  // REAL ESTATE (4 templates)
  createTemplate('real-estate-brokerage', 'Real Estate Brokerage', 'real-estate', 'Property sales and leasing', '🏡', 72,
    ['Transactions', 'Average Commission', 'Agent Count', 'Market Share'],
    [{ name: 'Commissions', category: 'service', avgUnitPrice: 8000, avgUnitsPerMonth: 10, growthRate: 15 }],
    [{ name: 'Agent Splits', category: 'variable', avgAmount: 0, percentage: 70 }, { name: 'Office', category: 'fixed', avgAmount: 5000 }],
    { grossMargin: { min: 25, avg: 30, max: 40 }, netMargin: { min: 8, avg: 15, max: 25 }, revenueGrowth: { min: 10, avg: 18, max: 30 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 50000, medium: 150000, high: 400000 }
  ),
  
  createTemplate('property-management', 'Property Management', 'real-estate', 'Residential or commercial property management', '🔑', 70,
    ['Units Managed', 'Revenue per Unit', 'Tenant Retention', 'Occupancy Rate'],
    [{ name: 'Management Fees', category: 'service', avgUnitPrice: 100, avgUnitsPerMonth: 200, growthRate: 12 }],
    [{ name: 'Staff', category: 'fixed', avgAmount: 12000 }, { name: 'Maintenance', category: 'variable', avgAmount: 2000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 18, avg: 30, max: 42 }, revenueGrowth: { min: 10, avg: 15, max: 25 }, breakEvenMonths: { min: 10, avg: 15, max: 20 }},
    { low: 40000, medium: 120000, high: 300000 }
  ),
  
  createTemplate('coworking', 'Coworking Space', 'real-estate', 'Shared workspace and office rentals', '💼', 73,
    ['Membership Count', 'Occupancy Rate', 'Revenue per Desk', 'Member Retention'],
    [{ name: 'Memberships', category: 'subscription', avgUnitPrice: 350, avgUnitsPerMonth: 100, growthRate: 18 }],
    [{ name: 'Rent', category: 'fixed', avgAmount: 12000 }, { name: 'Utilities', category: 'fixed', avgAmount: 3000 }],
    { grossMargin: { min: 45, avg: 55, max: 65 }, netMargin: { min: 12, avg: 22, max: 32 }, revenueGrowth: { min: 15, avg: 25, max: 40 }, breakEvenMonths: { min: 15, avg: 20, max: 30 }},
    { low: 100000, medium: 300000, high: 800000 }
  ),
  
  createTemplate('short-term-rental', 'Short-Term Rentals', 'real-estate', 'Airbnb and vacation rental management', '🏠', 75,
    ['Occupancy Rate', 'ADR', 'RevPAR', 'Guest Reviews'],
    [{ name: 'Rental Income', category: 'service', avgUnitPrice: 150, avgUnitsPerMonth: 500, growthRate: 20 }],
    [{ name: 'Cleaning', category: 'variable', avgAmount: 0, percentage: 15 }, { name: 'Mortgage/Rent', category: 'fixed', avgAmount: 8000 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 15, avg: 25, max: 35 }, revenueGrowth: { min: 18, avg: 30, max: 50 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 50000, medium: 200000, high: 600000 }
  ),
  
  // EDUCATION (4 templates)
  createTemplate('tutoring', 'Tutoring Service', 'education', 'Academic tutoring and test prep', '📚', 71,
    ['Student Count', 'Hours Tutored', 'Test Score Improvement', 'Retention Rate'],
    [{ name: 'Tutoring Sessions', category: 'service', avgUnitPrice: 60, avgUnitsPerMonth: 400, growthRate: 15 }],
    [{ name: 'Tutor Payments', category: 'variable', avgAmount: 15000 }, { name: 'Marketing', category: 'fixed', avgAmount: 1500 }],
    { grossMargin: { min: 50, avg: 60, max: 70 }, netMargin: { min: 20, avg: 30, max: 42 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 6, avg: 10, max: 15 }},
    { low: 15000, medium: 50000, high: 150000 }
  ),
  
  createTemplate('online-course', 'Online Course Platform', 'education', 'Digital course creation and sales', '🎓', 78,
    ['Course Sales', 'Student Completion', 'Course Rating', 'Refund Rate'],
    [{ name: 'Course Sales', category: 'product', avgUnitPrice: 199, avgUnitsPerMonth: 200, growthRate: 30 }],
    [{ name: 'Content Creation', category: 'fixed', avgAmount: 5000 }, { name: 'Platform Fees', category: 'variable', avgAmount: 0, percentage: 10 }],
    { grossMargin: { min: 75, avg: 85, max: 92 }, netMargin: { min: 30, avg: 50, max: 70 }, revenueGrowth: { min: 35, avg: 70, max: 150 }, breakEvenMonths: { min: 8, avg: 12, max: 18 }},
    { low: 10000, medium: 50000, high: 150000 }
  ),
  
  createTemplate('training-center', 'Training Center', 'education', 'Professional skills training facility', '🏫', 68,
    ['Enrollment', 'Course Completion', 'Job Placement', 'Student Satisfaction'],
    [{ name: 'Course Fees', category: 'service', avgUnitPrice: 2000, avgUnitsPerMonth: 30, growthRate: 15 }],
    [{ name: 'Instructors', category: 'fixed', avgAmount: 15000 }, { name: 'Facility', category: 'fixed', avgAmount: 6000 }],
    { grossMargin: { min: 55, avg: 65, max: 75 }, netMargin: { min: 18, avg: 28, max: 38 }, revenueGrowth: { min: 12, avg: 20, max: 35 }, breakEvenMonths: { min: 12, avg: 18, max: 24 }},
    { low: 80000, medium: 200000, high: 500000 }
  ),
  
  createTemplate('daycare', 'Daycare / Childcare', 'education', 'Early childhood education and care', '👶', 69,
    ['Enrollment', 'Staff Ratio', 'Occupancy', 'Parent Satisfaction'],
    [{ name: 'Monthly Tuition', category: 'subscription', avgUnitPrice: 1200, avgUnitsPerMonth: 40, growthRate: 10 }],
    [{ name: 'Staff Salaries', category: 'fixed', avgAmount: 18000 }, { name: 'Supplies', category: 'fixed', avgAmount: 2000 }],
    { grossMargin: { min: 45, avg: 52, max: 60 }, netMargin: { min: 10, avg: 18, max: 28 }, revenueGrowth: { min: 8, avg: 12, max: 20 }, breakEvenMonths: { min: 15, avg: 20, max: 30 }},
    { low: 80000, medium: 200000, high: 500000 }
  )
];

export function getMoreTemplates(): IndustryTemplate[] {
  return MORE_TEMPLATES;
}
